
import React from 'react';
import { useGame } from '../../contexts/GameContext';
import { CareerPath } from '../../types/game';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CheckIcon, ChartBarIcon, BriefcaseIcon, CodeIcon, FlaskConical } from 'lucide-react';
import { motion } from 'framer-motion';

interface CareerPathSelectionProps {
  open: boolean;
  onClose: () => void;
}

const CareerPathSelection: React.FC<CareerPathSelectionProps> = ({ open, onClose }) => {
  const { selectedRole, careerLevel, skills, selectCareerPath } = useGame();
  const [selectedPath, setSelectedPath] = React.useState<CareerPath>('Standard');
  
  const currentCareerLevelData = selectedRole?.careerPath.find(path => path.level === careerLevel);
  const pathOptions = currentCareerLevelData?.pathOptions || [];
  
  // Check which paths are available based on skills
  const getPathAvailability = (path: typeof pathOptions[number]) => {
    const requiredSkillIds = path.requiredSkills;
    const playerSkillIds = skills.map(skill => skill.id);
    
    return requiredSkillIds.every(skillId => playerSkillIds.includes(skillId));
  };
  
  const handlePathSelect = () => {
    selectCareerPath(selectedPath);
    onClose();
  };
  
  const getPathIcon = (path: CareerPath) => {
    switch (path) {
      case 'Technical':
        return <CodeIcon className="h-5 w-5 text-web3-green" />;
      case 'Management':
        return <BriefcaseIcon className="h-5 w-5 text-bitcoin-primary" />;
      case 'Entrepreneurial':
        return <ChartBarIcon className="h-5 w-5 text-web3-blue" />;
      case 'Research':
        return <FlaskConical className="h-5 w-5 text-bitcoin-accent" />;
      default:
        return <CheckIcon className="h-5 w-5 text-muted-foreground" />;
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[550px] p-6 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl">Choose Your Career Specialization</DialogTitle>
          <DialogDescription className="text-base mt-2">
            Your performance and skills have unlocked new career path options. Choose a specialization to gain unique benefits.
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4">
          <RadioGroup value={selectedPath} onValueChange={(value) => setSelectedPath(value as CareerPath)}>
            <div className="space-y-4">
              {pathOptions.map((path, index) => {
                const isAvailable = getPathAvailability(path);
                
                return (
                  <motion.div
                    key={path.path}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div 
                      className={`flex items-start space-x-3 rounded-md border p-4 ${
                        isAvailable 
                          ? 'cursor-pointer hover:border-bitcoin-primary/30 hover:bg-bitcoin-primary/5'
                          : 'opacity-60 cursor-not-allowed'
                      } ${selectedPath === path.path ? 'border-bitcoin-primary bg-bitcoin-primary/10' : ''}`}
                      onClick={() => isAvailable && setSelectedPath(path.path)}
                    >
                      <RadioGroupItem value={path.path} id={path.path} disabled={!isAvailable} />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center">
                          {getPathIcon(path.path)}
                          <Label 
                            htmlFor={path.path} 
                            className={`font-medium text-lg ml-2 ${!isAvailable ? 'text-muted-foreground' : ''}`}
                          >
                            {path.title}
                          </Label>
                        </div>
                        <div className="text-sm text-muted-foreground">{path.description}</div>
                        
                        <div className="pt-2">
                          <div className="text-xs font-medium uppercase text-muted-foreground">Boosts:</div>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            {Object.entries(path.kpiBoosts).map(([key, value]) => (
                              <div key={key} className="text-sm flex items-center">
                                <span className="text-bitcoin-accent">+{value}</span>
                                <span className="ml-1">{key}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {!isAvailable && (
                          <div className="text-xs text-destructive mt-2">
                            Required skills: {path.requiredSkills.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </RadioGroup>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handlePathSelect}>Confirm Specialization</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CareerPathSelection;
